import { onScopeDispose } from 'vue';
import { fastSearch, fastSearchByMySaju } from '@/api/Eoway';
import { getDetail } from '@/api/UserGroup';
import { DEV_MODE } from '@/config/dev-mode';
import { useAnalysisStore } from '@/store/modules/analysis';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { useMobileSajuLnbStore } from '@/store/modules/mobileSajuLnb';
import { usePrimarySajuStore } from '@/store/modules/primarySaju';

/** 상세분석에서의 만세력 데이터 Composable */
export const useSajuData = () => {
  const analysisStore = useAnalysisStore();
  const analysisDataStore = useAnalysisDataStore();
  const mobileSajuLnbStore = useMobileSajuLnbStore();
  const primarySajuStore = usePrimarySajuStore();
  let disposed = false;
  let requestId = 0;
  onScopeDispose(() => { disposed = true; requestId += 1; });

  /** 만세력 조회 param 데이터 요청 */
  const getSajuParams = async (guestUserId) => {
    try {
      const result = await getDetail(guestUserId);
      return result;
    } catch (e) {
      DEV_MODE && console.error('사주정보 조회 오류:', e);
      return null;
    }
  };

  /** 만세력 조회 param 데이터 가공 함수 */
  const formatSajuParams = (params, base = {}) => {
    const dateTime = params.integrationBirth.split('T');
    const [year, month, day] = dateTime[0].split('-');
    const [hour, min] = dateTime[1]?.split(':');

    const formatted = {
      ...base,
      birth: `${year}-${month}-${day}`,
      year,
      month: month.padStart(2, '0'),
      day: day.padStart(2, '0'),
      hour: hour.padStart(2, '0'),
      min: min.padStart(2, '0'),
      gender: params.gender === 'MEN' ? 1 : 0,
      userName: params.guestUserName,
      birthTime: params.birthTime,
      unknownTime: params.birthTime === '25:00' ? 'Y' : 'N',
      isLunar: params.isLuna ?? false,
      isLeapYear: params.isLeapYear ?? false,
      addressTxt: params.bornArea,
      bornAreaId: params.bornAreaId ?? undefined,
      option1: params.option1,
      option2: params.option2,
      option3: params.option3,
      option4: params.option4,
      option5: params.option5,
    };

    return formatted;
  };

  /** 만세력 조회 함수 */
  const getSajuData = async (paramData) => {
    try {
      const result = await fastSearch(paramData);
      return result;
    } catch (e) {
      return null;
    }
  };

  const isLoadingSajuData = ref(false);

  const FAILED_DEFAULT_MESSAGE = '사주 데이터 처리 중\n오류가 발생하였습니다.';

  /** 마지막으로 로드한 guestUserId */
  const lastLoadedGuestUserId = ref(null);

  /** 사주 데이터 초기화 (공통) */
  const initializeSajuData = async (guestUserId) => {
    const request = ++requestId;
    const isCurrent = () => !disposed && request === requestId;
    if (!isCurrent()) return { cancelled: true };
    // store에 동일 guestUserId의 sajuData가 이미 있으면 API 호출 생략
    const cached = analysisDataStore.sajuData;
    if (cached?.inputdata && lastLoadedGuestUserId.value === Number(guestUserId)) {
      if (cached.inputdata.unknown_time === 'Y') return { unknownTime: true };
      if (Number(cached.inputdata.year) > new Date().getFullYear()) return { futureSaju: true };

      const userName = cached.inputdata?.name;
      analysisStore.setUserName(userName);
      mobileSajuLnbStore.setUserName(userName);
      return;
    }

    isLoadingSajuData.value = true;
    try {
      const sajuParams = await getSajuParams(guestUserId);
      if (!isCurrent()) return { cancelled: true };
      if (!sajuParams) return { failed: true, message: '사주 상세정보를 찾을 수 없습니다.' };

      const isMine =
        sajuParams.isMySaju === true ||
        primarySajuStore.isMySaju(Number(guestUserId));
      const sajuResult = isMine
        ? await fastSearchByMySaju(Number(guestUserId), sajuParams.guestUserName).catch(() => null)
        : await getSajuData(formatSajuParams(sajuParams, analysisDataStore.paramData));
      if (!isCurrent()) return { cancelled: true };
      if (!sajuResult) return { failed: true, message: '사주 조회 요청에 실패하였습니다.' };

      if (sajuResult.inputdata.unknown_time === 'Y') {
        return { unknownTime: true };
      }
      if (Number(sajuResult.inputdata.year) > new Date().getFullYear()) {
        return { futureSaju: true };
      }

      const userName = sajuResult?.inputdata?.name;
      analysisStore.setUserName(userName);
      mobileSajuLnbStore.setUserName(userName);
      analysisDataStore.setSajuData(sajuResult);
      lastLoadedGuestUserId.value = Number(guestUserId);
    } catch (e) {
      if (!isCurrent()) return { cancelled: true };
      DEV_MODE && console.error('상세분석 > 사주정보 요청 실패:', e);
      return { failed: true, message: FAILED_DEFAULT_MESSAGE };
    } finally {
      if (isCurrent()) isLoadingSajuData.value = false;
    }
  };

  /** 사주 데이터 초기화 + 제한 사주 검증 및 alert */
  const initWithValidation = async (guestUserId, { showAlert, onRestricted }) => {
    const result = await initializeSajuData(guestUserId);
    if (result?.cancelled) return result;
    if (result?.failed) {
      showAlert({
        title: result.message || FAILED_DEFAULT_MESSAGE,
        nextUrl: onRestricted,
      });
      return result;
    }
    if (result?.unknownTime) {
      showAlert({
        title: '시간 모름인 사주는\n상세분석 서비스 제공이 어렵습니다.',
        nextUrl: onRestricted,
      });
      return result;
    }
    if (result?.futureSaju) {
      showAlert({
        title: '내년 이후 사주는\n상세분석 서비스 제공이 어렵습니다.',
        nextUrl: onRestricted,
      });
      return result;
    }
    return result;
  };

  return {
    /** 만세력 param 요청 */
    getSajuParams,
    /** 만세력 param 가공 함수 */
    formatSajuParams,
    /** 만세력 조회 함수 */
    getSajuData,
    /** 사주 데이터 초기화 */
    initializeSajuData,
    /** 사주 데이터 초기화 + 제한 사주 검증 */
    initWithValidation,
    /** 사주 데이터 로딩 상태 */
    isLoadingSajuData,
  };
};
